'use client';

import { BoxScrolled, usePortalContext } from '@cloai/uikit';
import { SyntheticEvent } from 'react';

import { PORTALS } from '@/.constants/portals';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
} from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';

import { PackPreview } from './PackPreview';

const styles = {
  wrapper: 'flex flex-col gap-3 scroll-mt-[150px]',
  line: 'flex items-center justify-between',
  lineInner: 'flex items-center gap-2',
  lineTitle: 'text-base font-bold',
  lineIcon: 'w-5 h-5',
  lineCount: 'text-sm font-medium text-label-secondary',
  head: 'flex items-center justify-between mb-3',
  name: 'text-sm font-semibold',
};

export const TrendingItems = () => {
  const profile = useProfileContext()!;
  const { openPortal } = usePortalContext();
  const sendGTM = useGTMContext();
  const vitrinaPosition = useVitrinaPosition('trending');
  const {
    genderMode,
    setPortalSeeAllMode,
    changeActiveCategory,
    trendingItems,
    effects,
    stickers,
    aiVideos,
    videoStyleCategories,
    styleCategories,
    setPrevPortal,
    effectsCategories,
    changeActiveStyle,
  } = useStyleContext();

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');

    const currentTrending = trendingItems?.find((item) => item.id === styleId);

    const currentIndex = trendingItems?.findIndex(
      (style) => style.id === styleId,
    );

    let category;
    if (currentTrending?.type! === 'StyleCategory') {
      category = styleCategories.find(
        (category) => category.id === currentTrending?.relationId,
      );

      if (!category) {
        category = effectsCategories.find(
          (cat) => cat.id === currentTrending?.relationId,
        );
      }

      if (category?.styles?.length) {
        changeActiveCategory(currentTrending?.relationId!, 'style');
      } else if (category?.effects?.length) {
        changeActiveCategory(currentTrending?.relationId!, 'effects');
      } else {
        changeActiveCategory(currentTrending?.relationId!, 'video');
      }
    }

    let effect;
    if (currentTrending?.type === 'Effect') {
      effect = effects.find(
        (effect) => effect.id === currentTrending?.relationId,
      );

      if (!effect) {
        effect = stickers.find(
          (sticker) => sticker.id === currentTrending?.relationId,
        );
      }

      if (!effect) {
        effect = effectsCategories
          .flatMap((category) => category.effects)
          .find((style) => style.id === currentTrending?.relationId);
        changeActiveCategory(effect?.styleCategoryId!, 'effects');
      }
    }

    let video;
    if (['VideoLook', 'VideoStyle'].includes(currentTrending?.type!)) {
      video = aiVideos.find(
        (video) => video.id === currentTrending?.relationId,
      );

      if (!video) {
        video = videoStyleCategories
          .flatMap((category) => category.aiVideos)
          .find((style) => style.id === currentTrending?.relationId);
        changeActiveCategory(video?.styleCategoryId!, 'video');
      }
    }

    const portalType =
      currentTrending?.type === 'StyleCategory'
        ? category
          ? category?.effects?.length
            ? 'effectsCategories'
            : 'styles'
          : 'videoStyleCategories'
        : currentTrending?.type === 'PhotoSession'
          ? 'photoSessions'
          : ['VideoLook', 'VideoStyle'].includes(currentTrending?.type!)
            ? video?.styleCategoryId
              ? 'videoStyleCategories'
              : 'aiVideos'
            : currentTrending?.type === 'Effect'
              ? effect?.isStickerPack
                ? 'stickers'
                : effect?.styleCategoryId
                  ? 'effectsCategories'
                  : 'effects'
              : 'videoStyles';

    setPrevPortal('main');
    setPortalSeeAllMode(portalType);
    changeActiveStyle(currentTrending?.relationId!, portalType);

    if (['StyleCategory'].includes(currentTrending?.type!)) {
      openPortal(PORTALS.SeeAllImages);
    } else {
      sendGTM({
        event: 'menu_selection',
        block: 'Актуальное',
        type: currentTrending?.type,
        category_name: null,
        name: currentTrending?.name['en'],
        category_position: currentIndex + 1,
        vitrina_position: vitrinaPosition,
        gender: genderMode,
      });

      openPortal(PORTALS.GenerateImage);
    }
  };

  if (!trendingItems.length) {
    return null;
  }

  // if (!profile?.isAdmin && type === 'videoStyles') {
  //   return null;
  // }

  return (
    <div className={styles.wrapper} id="trending">
      <BoxScrolled direction="horizontal" hideScrollbar gap={8} offsetOut={20}>
        {trendingItems?.map((item, i) => (
          <PackPreview
            key={item.id}
            id={item.id}
            index={i}
            name={item.name[profile?.lang]}
            image={getImageByGender(item, genderMode)!}
            handleClickImage={handleClickImage}
            isVideo
            isActual
            isTrending
          />
        ))}
      </BoxScrolled>
    </div>
  );
};
